import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout.js';
import { useAuth } from '../auth/AuthProvider.js';
import { setVisibilityAwareInterval } from '../lib/visibility-aware-interval.js';
import { EditVideoMetadataModal } from '../components/video/EditVideoMetadataModal.js';

/**
 * Training-video upload + browse surface. Mirrors the OneView (Galaxy)
 * design system already used by Documents / AdminKnowledgebase.
 *
 *   - Drag-drop a .mp4/.mov/.webm/.mkv up to 2 GB.
 *   - Frontend POSTs `/api/v1/videos/upload-url`, PUTs to the signed URL,
 *     then POSTs `/api/v1/videos/:id/ingest` and polls `/api/v1/videos/:id`
 *     every 5s for the pipeline state machine
 *     (transcoding → transcribing → summarising → indexed | failed).
 *   - Click a row to open the player modal: `<video controls>` pointed at
 *     `/api/v1/videos/:id/stream` + chapter list (seek on click) + transcript
 *     search-in-page.
 *
 * Admin-only (kb_admin). Non-admins see Documents and a help line.
 */

const VIDEO_MIME_TYPES = ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-matroska'];
const ACCEPT = '.mp4,.mov,.webm,.mkv';

type VideoStatus =
  | 'pending_upload'
  | 'transcoding'
  | 'transcribing'
  | 'summarising'
  | 'indexed'
  | 'failed';

interface VideoChapter {
  index: number;
  title: string;
  startSeconds: number;
  endSeconds: number;
  visualSummary?: string;
}

interface VideoRow {
  id: string;
  title: string;
  originalFilename: string;
  mimeType: string;
  sizeBytes: number;
  status: VideoStatus;
  ring: 'ring0' | 'ring1';
  uploadedAt: string;
  indexedAt?: string;
  durationSeconds?: number;
  chapterCount?: number;
  chapters?: VideoChapter[];
  errorMessage?: string;
  language?: string;
  audioPresent?: boolean;
  // Library metadata (set via the EditVideoMetadataModal after ingest).
  seriesId?: string;
  episode?: number;
  totalEpisodes?: number;
  isFeatured?: boolean;
  presenter?: { name: string; role?: string };
  resources?: { kind: 'doc' | 'link'; title: string; documentId?: string; href?: string }[];
  publishedAt?: string;
}

const STATUS_LABEL: Record<VideoStatus, string> = {
  pending_upload: 'Uploading',
  transcoding: 'Extracting audio',
  transcribing: 'Transcribing',
  summarising: 'Summarising',
  indexed: 'Indexed',
  failed: 'Failed',
};

function fmtDuration(seconds?: number): string {
  if (!seconds || seconds <= 0) return '—';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function fmtBytes(n: number): string {
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export function VideosPage(): JSX.Element {
  const { user } = useAuth();
  const [videos, setVideos] = useState<VideoRow[]>([]);
  const [uploading, setUploading] = useState<{ name: string; progress: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<VideoRow | null>(null);
  const [editing, setEditing] = useState<VideoRow | null>(null);
  const [searchParams] = useSearchParams();
  // Citation deep-link: /videos?id=<videoId>&t=<seconds> auto-opens the modal
  // at the moment. Also supports /videos/:id?t=<seconds> via the rail link;
  // for now we expose ?id= so a single mount handles both shapes.
  const deepLinkVideoId = searchParams.get('id');
  const deepLinkSeconds = Number.parseInt(searchParams.get('t') ?? '', 10);
  const fileRef = useRef<HTMLInputElement>(null);

  const refresh = useCallback(async () => {
    const res = await fetch('/api/v1/videos', { credentials: 'include' });
    if (!res.ok) return;
    const data: { videos: VideoRow[] } = await res.json();
    setVideos(data.videos);
  }, []);

  const reingest = useCallback(
    async (videoId: string) => {
      try {
        const res = await fetch(`/api/v1/videos/${videoId}/reingest`, {
          method: 'POST',
          credentials: 'include',
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          alert(err.message || 'Could not start re-ingest.');
          return;
        }
        await refresh();
      } catch (e) {
        alert(e instanceof Error ? e.message : 'Re-ingest request failed.');
      }
    },
    [refresh],
  );

  useEffect(() => {
    if (deepLinkVideoId && !selected) {
      const match = videos.find((v) => v.id === deepLinkVideoId);
      if (match) setSelected(match);
    }
  }, [deepLinkVideoId, videos, selected]);

  useEffect(() => {
    void refresh();
    // Poll while anything is mid-ingest. 5s cadence is comfortable for the
    // 1-30 min ingest range; we throttle when nothing is in-flight, and
    // pause entirely while the tab is hidden (see visibility-aware-interval
    // — backgrounded tabs were the dominant contributor to the 2026-05-18
    // rate-limit storm).
    const hasInFlight = videos.some((v) => v.status !== 'indexed' && v.status !== 'failed');
    if (!hasInFlight) return;
    return setVisibilityAwareInterval(() => {
      void refresh();
    }, 5000);
  }, [refresh, videos]);

  const onPickFile = (file: File): void => {
    setError(null);
    if (!VIDEO_MIME_TYPES.includes(file.type)) {
      setError(`Unsupported video type: ${file.type || 'unknown'}`);
      return;
    }
    if (file.size > 2 * 1024 * 1024 * 1024) {
      setError('File larger than 2 GB.');
      return;
    }
    void uploadOne(file);
  };

  async function uploadOne(file: File): Promise<void> {
    setUploading({ name: file.name, progress: 0 });
    try {
      const mintRes = await fetch('/api/v1/videos/upload-url', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          title: file.name.replace(/\.[^.]+$/, ''),
          originalFilename: file.name,
          mimeType: file.type,
          sizeBytes: file.size,
          ring: 'ring1',
        }),
      });
      if (!mintRes.ok) {
        const err = await mintRes.json().catch(() => ({}));
        throw new Error(err.message || 'Could not create upload URL.');
      }
      const { videoId, uploadUrl } = (await mintRes.json()) as {
        videoId: string;
        uploadUrl: string;
      };

      // Direct PUT to the GCS signed URL. XHR so we get progress events.
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.upload.onprogress = (evt) => {
          if (evt.lengthComputable) {
            setUploading({ name: file.name, progress: evt.loaded / evt.total });
          }
        };
        xhr.onload = () =>
          xhr.status >= 200 && xhr.status < 300
            ? resolve()
            : reject(new Error(`upload failed: ${xhr.status}`));
        xhr.onerror = () => reject(new Error('upload network error'));
        xhr.send(file);
      });

      const startRes = await fetch(`/api/v1/videos/${videoId}/ingest`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!startRes.ok) {
        const err = await startRes.json().catch(() => ({}));
        throw new Error(err.message || 'Could not start ingest.');
      }
      setUploading(null);
      void refresh();
    } catch (exc) {
      setUploading(null);
      setError(exc instanceof Error ? exc.message : String(exc));
    }
  }

  const isAdmin = user?.role === 'kb_admin';

  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-6 py-8">
        <header className="mb-6">
          <h1 className="text-2xl font-semibold text-nebula-fg-1">Training videos</h1>
          <p className="mt-1 text-sm text-nebula-fg-2">
            Upload a training recording. The pipeline transcribes the audio (English), summarises
            each chapter, and indexes everything so the Knowledge Agent can cite the moment in the
            video.
          </p>
        </header>

        {isAdmin && (
          <div
            className="mb-6 rounded-lg border-2 border-dashed border-nebula-secondary/40 bg-nebula-tint p-8 text-center"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const f = e.dataTransfer.files?.[0];
              if (f) onPickFile(f);
            }}
          >
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPT}
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onPickFile(f);
                e.target.value = '';
              }}
            />
            <button
              type="button"
              className="rounded-md bg-nebula-primary px-4 py-2 text-sm font-medium text-white hover:bg-nebula-secondary"
              onClick={() => fileRef.current?.click()}
            >
              Upload training video
            </button>
            <p className="mt-3 text-xs text-nebula-fg-2">
              Drag and drop .mp4, .mov, .webm, or .mkv. Up to 2 GB.
            </p>
            {uploading && (
              <p className="mt-3 text-xs text-nebula-fg-1">
                Uploading {uploading.name}: {Math.round(uploading.progress * 100)}%
              </p>
            )}
            {error && (
              <p className="mt-3 text-xs text-red-600" role="alert">
                {error}
              </p>
            )}
          </div>
        )}

        <div className="overflow-hidden rounded-lg border border-nebula-border bg-white">
          <table className="w-full text-sm">
            <thead className="bg-nebula-tint text-left text-xs uppercase text-nebula-fg-2">
              <tr>
                <th className="px-4 py-2">Title</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Duration</th>
                <th className="px-4 py-2">Chapters</th>
                <th className="px-4 py-2">Size</th>
                <th className="px-4 py-2">Uploaded</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {videos.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-nebula-fg-2">
                    No training videos yet.
                  </td>
                </tr>
              )}
              {videos.map((v) => {
                const canReingest = v.status === 'indexed' || v.status === 'failed';
                return (
                  <tr
                    key={v.id}
                    className="cursor-pointer border-t border-nebula-border hover:bg-nebula-tint"
                    onClick={() => setSelected(v)}
                  >
                    <td className="px-4 py-2 text-nebula-fg-1">{v.title}</td>
                    <td className="px-4 py-2">{STATUS_LABEL[v.status]}</td>
                    <td className="px-4 py-2 font-mono text-xs">
                      {fmtDuration(v.durationSeconds)}
                    </td>
                    <td className="px-4 py-2">{v.chapterCount ?? '—'}</td>
                    <td className="px-4 py-2">{fmtBytes(v.sizeBytes)}</td>
                    <td className="px-4 py-2 text-xs text-nebula-fg-2">
                      {new Date(v.uploadedAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="inline-flex items-center gap-1">
                        <button
                          type="button"
                          disabled={v.status !== 'indexed'}
                          onClick={() => setEditing(v)}
                          title="Edit library metadata (series, episode, presenter, resources)"
                          className="rounded border border-nebula-border bg-white px-2 py-1 text-xs text-nebula-fg-1 hover:bg-nebula-tint disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          Edit metadata
                        </button>
                        <button
                          type="button"
                          disabled={!canReingest}
                          onClick={() => {
                            if (
                              window.confirm(
                                `Re-ingest "${v.title}"? Existing chunks will be replaced.`,
                              )
                            ) {
                              void reingest(v.id);
                            }
                          }}
                          className="rounded border border-nebula-border bg-white px-2 py-1 text-xs text-nebula-fg-1 hover:bg-nebula-tint disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          Re-ingest
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <VideoPlayerModal
          video={selected}
          onClose={() => setSelected(null)}
          initialSeconds={
            Number.isFinite(deepLinkSeconds) && deepLinkSeconds >= 0 ? deepLinkSeconds : undefined
          }
        />
      )}

      {editing && (
        <EditVideoMetadataModal
          video={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            void refresh();
          }}
        />
      )}
    </Layout>
  );
}

function VideoPlayerModal({
  video,
  onClose,
  initialSeconds,
}: {
  video: VideoRow;
  onClose: () => void;
  initialSeconds?: number;
}): JSX.Element {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (typeof initialSeconds === 'number' && videoRef.current) {
      videoRef.current.currentTime = initialSeconds;
    }
  }, [initialSeconds]);

  const seekTo = (seconds: number): void => {
    if (videoRef.current) {
      videoRef.current.currentTime = seconds;
      void videoRef.current.play();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-4xl overflow-hidden rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-nebula-border px-4 py-3">
          <h2 className="text-base font-semibold text-nebula-fg-1">{video.title}</h2>
          <button onClick={onClose} className="text-sm text-nebula-fg-2 hover:text-nebula-fg-1">
            Close
          </button>
        </div>
        <video
          ref={videoRef}
          controls
          className="aspect-video w-full bg-black"
          src={`/api/v1/videos/${video.id}/stream`}
          // crossOrigin omitted: cookie-auth + same-origin in production.
        />
        {video.audioPresent === false && (
          <div className="border-t border-nebula-border bg-nebula-tint px-4 py-2 text-xs text-nebula-fg-2">
            Silent video — indexed from on-screen content only.
          </div>
        )}
        {video.chapters && video.chapters.length > 0 && (
          <div className="max-h-64 overflow-y-auto border-t border-nebula-border">
            <ul>
              {video.chapters.map((ch) => (
                <li key={ch.index} className="border-b border-nebula-border last:border-b-0">
                  <button
                    onClick={() => seekTo(ch.startSeconds)}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-nebula-tint"
                  >
                    <span className="font-mono text-xs text-nebula-fg-2">
                      {fmtDuration(ch.startSeconds)}–{fmtDuration(ch.endSeconds)}
                    </span>{' '}
                    <span className="text-nebula-fg-1">{ch.title}</span>
                    {ch.visualSummary && (
                      <p className="mt-1 text-xs text-nebula-fg-2">{ch.visualSummary}</p>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
